type NodeInfo = {
  name: string
  probabilities: number[]
}
export type NodeInfoProbabilityDict = {
  [variableId: string]: NodeInfo
}
type BayesianExpectation = Record<
  number,
  {
    expected_value: number
    tolerance_value: number
  }
>
export type BayesianAssumption = {
  variableId: string
  // null is the "unobserved" sentinel: the variable is part of the scenario but
  // has no fixed evidence. Kept (not omitted) so an all-unobserved set stays
  // non-empty for the parentless-node handling in the optimize pipeline.
  observedState: number | null
  // Virtual (probability-weighted) evidence over the variable's states, derived
  // from a researcher's numeric assumption value (KBE-774). Mutually exclusive
  // with a non-null `observedState` — the API's Pydantic model rejects a payload
  // setting both (models/optimization/optimization_request.py), so the builder
  // sends `observedState: null` alongside a vector. Optional here rather than
  // always-present so hard-evidence assumption sets serialize byte-identically
  // to before this field existed.
  virtualEvidence?: number[]
}
export type BayesianAssumptionSet = {
  id: string
  name: string
  assumptions: BayesianAssumption[]
  requirements: Record<string, BayesianExpectation>
}

// Evidence for one variable in an inference request: either a hard observed
// state index (existing behavior) or a probability vector over the variable's
// states (virtual evidence, KBE-651/KBE-654). The bayesian-api accepts the
// union (`dict[str, int | list[float]]`); index order matches the state order
// KBE sends stateCount + the flattened CPT in.
export type AssumptionEvidence = number | number[]

// Which engine runs inference for a network (KBE-840). PGMax LBP is the only
// engine there is: the bayesian-api has had no other since KBE-712 removed SMILE,
// and KBE's own engine is a TypeScript port of that same PGMax LBP. So no member
// names an algorithm -- each names WHERE inference runs:
//
//   LBP_ON_MODAL   -- the bayesian-api, hosted on Modal, over HTTP.
//   LBP_IN_BROWSER -- @ancestralize/bayesian-utils in a Web Worker where a
//                     browser is in scope, server-side where none is.
//   LBP_ON_SERVER  -- @ancestralize/bayesian-utils, always server-side.
//
// Named for the location rather than the algorithm since KBE-840's follow-up: the
// old PGMAX_LBP named an algorithm while its two siblings named a location, which
// is what made "which of these is a different algorithm?" a question at all. The
// answer was always "none of them". LBP_ON_SERVER deliberately names the TIER and
// not the host -- it is equally true in local dev, backend tests and CI, none of
// which is Vercel.
//
// The bayesian-api never sees these values. Its infer/infers/diagnose models
// declare `inference_algorithm: Optional[InferenceAlgorithm] = None` and then
// build a PGMaxInferer without reading it, so KBE stopped sending the field
// rather than keeping its vocabulary tied to that (optimizer) enum.
export enum InferenceAlgorithm {
  LBP_ON_MODAL = 'LBP_ON_MODAL',
  LBP_IN_BROWSER = 'LBP_IN_BROWSER',
  LBP_ON_SERVER = 'LBP_ON_SERVER',
}

// The engines the Bayesian performance cron measures, and therefore the series
// `/performance` charts. LBP_IN_BROWSER is absent deliberately: it names the same
// engine as LBP_ON_SERVER and differs only in WHERE it runs, so measuring it
// server-side would record a duplicate row under a second name and draw an empty
// series (KBE-840).
export const MEASURED_INFERENCE_ENGINES = [
  InferenceAlgorithm.LBP_ON_MODAL,
  InferenceAlgorithm.LBP_ON_SERVER,
]

// "Local" is KBE's own engine, wherever it runs -- the contrast is with Modal, not
// with the server, so LBP_ON_SERVER is local too.
const LOCAL_INFERENCE_ALGORITHMS = new Set<string>([
  InferenceAlgorithm.LBP_IN_BROWSER,
  InferenceAlgorithm.LBP_ON_SERVER,
])

// Takes a raw string because that is what callers hold: `network.inferenceAlgorithm`
// reaches several of them through a plain `as InferenceAlgorithm` cast over the
// free-text column. Anything unrecognized is not local, so it routes to Modal -- the
// same answer the column's legacy free-text values have always produced.
export function isLocalInferenceAlgorithm(
  algorithm: string | null | undefined,
): boolean {
  return !!algorithm && LOCAL_INFERENCE_ALGORITHMS.has(algorithm)
}

// KBE-766: the optimizer strategy, sent as the optimize request's
// options.inference_algorithm -- a misnomer inherited from the bayesian-api's
// Options model, tracked for renaming in KBE-780. These are NOT inference
// algorithms: each selects an optimizer implementation in the API's
// _run_optimization (JaxOptimizer / JaxoptOptimizer / scipy LocalOptimizer).
//
// PGMAX_LBP here is the OPTIMIZER value of that name and has nothing to do with
// the retired InferenceAlgorithm member. They were one string in two enums until
// the rename above; never fold them back together.
export enum OptimizationAlgorithm {
  PGMAX_LBP = 'PGMAX_LBP',
  PGMAX_LBP_GRADIENT = 'PGMAX_LBP_GRADIENT',
  JIT_JAXOPT_MIRROR_DESCENT = 'JIT_JAXOPT_MIRROR_DESCENT',
}

const ROUTABLE_INFERENCE_ALGORITHMS = new Set<string>(
  Object.values(InferenceAlgorithm),
)

const VALID_OPTIMIZATION_ALGORITHMS = new Set<string>(
  Object.values(OptimizationAlgorithm),
)

// The ROUTING coercion: which engine a network has selected. This is the only
// coercion left -- there is no wire counterpart, because the bayesian-api is no
// longer sent an engine at all (KBE-840 follow-up). A value that names nothing routes
// to Modal, which is what every legacy free-text value (SMILE_*, the retired
// optimizer values) has always done.
//
// The PRE-V148 spellings are deliberately NOT mapped. merge.yml runs db:migrate before
// vercel deploy for staging and production alike, and the app only writes members, so
// after the migration no row can hold one. The only reader that would ever see one is
// a preview deploy, which shares the staging DB as-is, with no migration -- not worth a
// permanent branch that would have to be deleted again.
export function coerceRoutedInferenceAlgorithm(
  algorithm: string | null | undefined,
): InferenceAlgorithm {
  if (algorithm && ROUTABLE_INFERENCE_ALGORITHMS.has(algorithm)) {
    return algorithm as InferenceAlgorithm
  }
  return InferenceAlgorithm.LBP_ON_MODAL
}

// KBE-766: the optimize path must NOT reuse the inference coercion -- that one
// answers a different question and would rewrite a researcher's chosen optimizer,
// silently moving every optimization onto the slow scipy LocalOptimizer. Defaults
// to the API's own Options default instead.
export function coerceOptimizationAlgorithm(
  algorithm: string | null | undefined,
): OptimizationAlgorithm {
  if (algorithm && VALID_OPTIMIZATION_ALGORITHMS.has(algorithm)) {
    return algorithm as OptimizationAlgorithm
  }
  return OptimizationAlgorithm.PGMAX_LBP_GRADIENT
}

export enum BayesianCalculationTypes {
  INFER = 'INFER',
  PARALLEL_INFERS = 'PARALLEL_INFERS',
  DIAGNOSTIC_VALUE = 'DIAGNOSTIC_VALUE',
}

export type VariableProbabilities = {
  variableId: string
  variableName: string
  probabilities: number[]
}
